import { useAppKit, useWalletInfo } from '@reown/appkit/react';
import fromExponential from 'from-exponential';
import { useCallback, useEffect, useMemo } from 'react';
import { Hex, parseUnits } from 'viem';
import {
  useAccount,
  useBalance,
  useDisconnect,
  useSendTransaction,
  useSwitchAccount,
  useSwitchChain,
  useWalletClient,
  useWriteContract,
} from 'wagmi';

import web3Abi from '../constants/web3.abi';
import { Webe3Chains } from '../constants/web3.constants';
import web3Protocols from '../constants/web3.protocols';
import { findContractByAssetCode } from '../utils/web3.utils';
import { Web3ChainId, Web3Transfer } from '../web3.types';

export function useWeb3() {
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const { sendTransactionAsync } = useSendTransaction();

  const {
    isConnected,
    isDisconnected,
    connector,
    address: walletAddress,
    chain: walletChain,
    chainId: walletChainId,
    isConnecting,
  } = useAccount();
  const { connectors } = useSwitchAccount();
  const { disconnectAsync } = useDisconnect();
  const { open } = useAppKit();
  const { data: balance } = useBalance({
    chainId: walletChainId,
    address: walletAddress,
  });

  const _connectors = useMemo(() => connectors.map(({ id, icon, name }) => ({ id, icon, name })), [connectors]);
  const _connector = useMemo(
    () => ({ id: connector?.id, icon: connector?.icon, name: connector?.name }),
    [connector?.icon, connector?.id, connector?.name],
  );

  const walletBalance = useMemo(
    () => (balance?.decimals ? Number(balance?.value) / 10 ** Number(balance?.decimals) : 0),
    [balance?.decimals, balance?.value],
  );

  const client = useWalletClient();
  const info = useWalletInfo();

  const wallet = useMemo(
    () => ({
      icon: info.walletInfo?.icon,
      name: info.walletInfo?.name,
    }),
    [info.walletInfo?.icon, info.walletInfo?.name],
  );

  useEffect(() => {
    if (isDisconnected) window.postMessage({ type: 'web3:disconnected' });
    if (isConnected)
      window.postMessage({
        type: 'web3:connected',
        ...(window.xboWeb3 || {}),
      });
  }, [
    _connector,
    _connectors,
    client.data?.account.address,
    connector,
    info.walletInfo?.icon,
    info.walletInfo?.name,
    isConnected,
    isDisconnected,
    walletAddress,
  ]);

  const switchChain = useCallback(
    async (_chainId: Web3ChainId) => await switchChainAsync({ chainId: _chainId }),
    [switchChainAsync],
  );

  useEffect(() => {
    if (!connector?.name || !client.data?.account.address) {
      window.xboWeb3 = undefined;
      return;
    }

    window.xboWeb3 = {
      connectors: _connectors,
      connector: {
        ..._connector,
        icon: _connector?.icon ?? info.walletInfo?.icon,
        name: _connector?.name ?? info.walletInfo?.name,
      },
      icon: _connector?.icon ?? info.walletInfo?.icon,
      name: _connector?.name ?? info.walletInfo?.name,
      address: client.data?.account.address,
      protocols: web3Protocols,
    };
  }, [
    _connector,
    _connectors,
    client.data?.account.address,
    connector?.name,
    info.walletInfo?.icon,
    info.walletInfo?.name,
  ]);

  const connect = useCallback(() => open({ view: 'Connect' }), [open]);

  const transfer = useCallback(
    async ({ address, assetCode, networkCode, amount }: Web3Transfer) => {
      if (!assetCode || !networkCode || !address) return;

      const contract = findContractByAssetCode(assetCode);

      if (!contract) {
        console.error(`Failed to find matching contract address for ${assetCode}`);
        return;
      }

      // console.log('contract => ', contract);

      const _chainId = Webe3Chains[networkCode];

      if (!_chainId) {
        console.error(`Unsupported networkCode: ${networkCode}`);
        return;
      }

      const chain = await switchChain(_chainId).catch((error) =>
        console.log('[web3] Switch chain. Error: ', error.code),
      );

      if (!chain) return;

      if (!contract.contractAddress) {
        return await sendTransactionAsync({
          to: address,
          connector,
          value: parseUnits(fromExponential(String(amount)), contract.decimals),
          account: walletAddress,
          chainId: chain.id,
        });
      }

      if (!contract?.decimals) return;

      return await writeContractAsync({
        abi: web3Abi,
        address: contract.contractAddress as Hex,
        chainId: _chainId,
        functionName: 'transfer',
        args: [address, parseUnits(fromExponential(String(amount)), contract.decimals)],
        account: walletAddress,
      }).catch((err) => console.log(err));
    },
    [connector, sendTransactionAsync, switchChain, walletAddress, writeContractAsync],
  );

  return {
    switchChain,
    chain: walletChain,
    balance: walletBalance,
    token: balance?.symbol,
    address: walletAddress,
    transfer,
    connect,
    connectors,
    disconnect: disconnectAsync,
    isConnected,
    isConnecting,
    connector,
    wallet,
  };
}
